import { AsyncSelect } from "components/AsyncInput/AsyncSelect";
import {
  showErrorToast,
  showSuccessToast,
} from "components/CustomAlerts/CustomToast";
import { Input } from "components/Input/Input";
import { Select } from "components/Select/Select";
import { CURSADO_OPTIONS } from "components/EspaciosTable/EspaciosTable";
import { useFormik } from "formik";
import { AreaI } from "interfaces/Area";
import { CicloI } from "interfaces/Ciclo";
import { DiseñoCurricularI } from "interfaces/DiseñoCurricular";
import { EspacioCurricularI } from "interfaces/EspacioCurricular";
import { NivelI } from "interfaces/Nivel";
import { TipoEspacioI } from "interfaces/TipoEspacio";
import { useEffect, useState } from "react";
import { Button, Card, Col, Modal, Row } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { PRIVATE_ROUTE } from "routes/privateRoutes";
import { createEspaciosCurricularesSchema } from "schemas/espaciosCurriculares/createEspaciosCurricularesSchema";
import { abmActionI } from "screens/Planes/PlanesABM/PlanesABM";
import { getArea } from "services/area";
import { getCiclo } from "services/ciclo";
import {
  postDiseñoCurricular,
  putDiseñoCurricular,
} from "services/diseñoCurricular";
import {
  getEspacioCurricularById,
  postEspacioCurricular,
  putEspacioCurricular,
} from "services/espacioCurricular";
import { getNiveles } from "services/niveles";
import { getTipoEspacio } from "services/tipoEspacio";
import { AppDispatch } from "store/store";
import { formatDate } from "utils/formatUtils";

interface Props {
  abmAction?: abmActionI;
}

interface initialValues {
  id?: string | number;
  nombre: string;
  descripcion: string;
  sub_tipo: string;
  con_sub_tipo: boolean;
  tipo_espacio_id: string | number;
  area_id: string | number;
  nivel_id: number | string;
  anio: number|string;
  horas_semanales: number |string;
  cuatrimestre: number | string; // "" = anual
  espacio_formacion: string;
  ciclo_id: number | string;
}

interface defaultOptionsSelected {
  default_tipo_espacio: TipoEspacioI | null;
  default_area: AreaI | null;
  default_selected_nivel: NivelI | null;
  default_selected_ciclo: CicloI | null;
}

export const EspaciosCurricularesABM = ({ abmAction = "create" }: Props) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch: AppDispatch = useDispatch();

  const [isLoading, setIsLoading] = useState({
    form: false,
  });

  const initialValues: initialValues = {
    nombre: "",
    descripcion: "",
    con_sub_tipo: false,
    sub_tipo: "Ninguno",
    tipo_espacio_id: "",
    area_id: "",
    nivel_id: "",
    anio: "",
    horas_semanales: "",
    cuatrimestre: "",
    espacio_formacion: "",
    ciclo_id: "",
  };

  const [defaultOptionsSelected, setDefaultOptionsSelected] =
    useState<defaultOptionsSelected>({
      default_area: null,
      default_tipo_espacio: null,
      default_selected_nivel: null,
      default_selected_ciclo: null,
    });

  const loadEditForm = async () => {
    const errorLoadingNotificacionResolve = () => {
      showErrorToast("No fue posible cargar el espacio curricular a editar");
      navigate(PRIVATE_ROUTE.ESPACIOS_CURRICULARES);
    };

    try {
      setIsLoading((st) => ({ ...st, form: true }));

      if (!id) return errorLoadingNotificacionResolve();

      const espacioCurricular = await getEspacioCurricularById(id);

      if (!espacioCurricular) return errorLoadingNotificacionResolve();

      let currentValues;

      currentValues = {
        id: espacioCurricular?.id ?? "",
        nombre: espacioCurricular?.nombre ?? "",
        descripcion: espacioCurricular?.descripcion ?? "",
        sub_tipo: espacioCurricular.tipo_espacio.con_sub_tipo
          ? espacioCurricular.sub_tipo
          : "Ninguno",
        con_sub_tipo: espacioCurricular.tipo_espacio.con_sub_tipo,
        tipo_espacio_id: espacioCurricular.tipo_espacio.id,
        area_id: espacioCurricular.area.id,
        nivel_id: espacioCurricular.nivel.id,
        anio: espacioCurricular.anio,
        horas_semanales: espacioCurricular.horas_semanales,
        cuatrimestre: espacioCurricular.cuatrimestre ?? "",
        espacio_formacion: espacioCurricular.espacio_formacion,
        ciclo_id: espacioCurricular?.ciclo?.id,
      };

      setDefaultOptionsSelected({
        default_tipo_espacio: espacioCurricular.tipo_espacio,
        default_area: espacioCurricular.area,
        default_selected_nivel: espacioCurricular.nivel,
        default_selected_ciclo: espacioCurricular.ciclo,
      });
      setValues(currentValues);
    } catch (err) {
      errorLoadingNotificacionResolve();
    } finally {
      setIsLoading((st) => ({ ...st, form: false }));
    }
  };

  const handleReturn = () => {
    navigate(PRIVATE_ROUTE.ESPACIOS_CURRICULARES);
  };

  const formatParams = (params: any) => {
    const formatedParams: { [key: string]: string } = {
      nombre: params.nombre,
      descripcion: params.descripcion,
      tipo_espacio_id: params.tipo_espacio_id,
      area_id: params.area_id,
      anio: params.anio,
      nivel_id: params.nivel_id,
      horas_semanales: params.horas_semanales,
      cuatrimestre: params.cuatrimestre, // "" se guarda como anual (null) en la API
      espacio_formacion: params.espacio_formacion,
      sub_tipo: "",
      ciclo_id: params.ciclo_id,
    };
    if (params.con_sub_tipo) formatedParams.sub_tipo = params.sub_tipo;
    if (id) formatedParams.id = id;

    return formatedParams;
  };

  const handleSubmitCreate = async (params: any) => {
    try {
      const formatedParams = formatParams(params);
      await postEspacioCurricular({ params: formatedParams });
      handleReturn();
      showSuccessToast("¡El espacio curricular fue creado con éxito!");
    } catch (err: any) {
      showErrorToast("El espacio curricular no pudo ser creado.");
      console.log(err.message);
    }
  };

  const handleSubmitEdit = async (params: any) => {
    try {
      const formatedParams = formatParams(params);
      await putEspacioCurricular({ params: formatedParams });
      handleReturn();
      showSuccessToast("¡El espacio curricular fue editado con éxito!");
    } catch (err: any) {
      showErrorToast("La espacio curricular no pudo ser editado.");
      console.log(err.message);
    }
  };

  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    isSubmitting,
    handleSubmit,
    setValues,
  } = useFormik({
    initialValues,
    validationSchema: createEspaciosCurricularesSchema,
    onSubmit: abmAction == "create" ? handleSubmitCreate : handleSubmitEdit,
  });

  const handleChangeTipoEspacio = (e: any, tipoEspacio: TipoEspacioI) => {
    if (tipoEspacio && tipoEspacio.con_sub_tipo) {
      const customEventConSubTipo = {
        target: { value: true, id: "con_sub_tipo" },
      };
      const customEventSubTipo = { target: { value: "", id: "sub_tipo" } };
      handleChange(customEventConSubTipo);
      handleChange(customEventSubTipo);
    } else {
      const customEventSubTipo = {
        target: { value: "Ninguno", id: "sub_tipo" },
      };
      const customEventConSubTipo = {
        target: { value: false, id: "con_sub_tipo" },
      };
      handleChange(customEventConSubTipo);
      handleChange(customEventSubTipo);
    }
    handleChange(e);
  };

  useEffect(() => {
    if (abmAction == "edit") loadEditForm();
  }, [abmAction]);

  return (
    <>
      <section>
        <h1 className="section-title">
          {abmAction == "edit"
            ? "Editar espacio curricular"
            : "Nuevo espacio curricular"}
        </h1>

        <h2 className="section-subtitle mt-2">
          Edita o crea un nuevo espacio curricular.
        </h2>
        <Card>
          <form onSubmit={handleSubmit}>
            <Row>
              <Col sm={12} md={6} className="mb-4">
                <Input
                  id={"nombre"}
                  title="Nombre del espacio curricular"
                  required
                  onChange={handleChange}
                  onBlur={handleBlur}
                  values={values}
                  errors={errors}
                  touched={touched}
                  variant="gray-container"
                  size="lg"
                />
              </Col>
              <Col sm={12} md={6} className="mb-4">
                <Input
                  id={"descripcion"}
                  title="Descripcion"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  values={values}
                  errors={errors}
                  touched={touched}
                  variant="gray-container"
                  size="lg"
                />
              </Col>

              <Col sm={12} md={4} className="mb-4">
                <Input
                  id={"anio"}
                  title="Año"
                  required
                  onChange={handleChange}
                  onBlur={handleBlur}
                  values={values}
                  errors={errors}
                  touched={touched}
                  variant="gray-container"
                  size="lg"
                  type="number"
                  min={1}
                />
              </Col>
              <Col sm={12} md={4} className="mb-4">
                <Input
                  id={"horas_semanales"}
                  title="Horas semanales"
                  required
                  onChange={handleChange}
                  onBlur={handleBlur}
                  values={values}
                  errors={errors}
                  touched={touched}
                  variant="gray-container"
                  size="lg"
                  type="number"
                  min={1}
                />
              </Col>
              <Col sm={12} md={4} className="mb-4">
                <Select
                  id="cuatrimestre"
                  title="Cursado"
                  required
                  size="lg"
                  options={CURSADO_OPTIONS}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  values={values}
                  errors={errors}
                  touched={touched}
                  variant="gray-container"
                  isSearchable={false}
                />
              </Col>

              <Col sm={12} md={6} lg={4} className="mb-4">
                <Input
                  id={"espacio_formacion"}
                  title="Espacio de formación"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  values={values}
                  errors={errors}
                  touched={touched}
                  variant="gray-container"
                  size="lg"
                />
              </Col>
              <Col sm={12} md={6} lg={4} className="mb-4">
                <AsyncSelect
                  onChange={handleChangeTipoEspacio}
                  id="tipo_espacio_id"
                  values={values}
                  errors={errors}
                  touched={touched}
                  onBlur={handleBlur}
                  placeholder={"Buscar por nombre"}
                  size="lg"
                  title="Tipo de espacio"
                  getter={getTipoEspacio}
                  paramKey={"nombre"}
                  variant="gray-container"
                  arrElementsPath="elementos"
                  optionsFormatConfig={{
                    value: "id",
                    label: (el: TipoEspacioI) => `${el.nombre}`,
                  }}
                  defaultSelectedOptions={
                    defaultOptionsSelected["default_tipo_espacio"]
                  }
                  required
                  noOptionsMessage={`No hay opciones disponibles para el nombre ingresado`}
                  emptySelectMessage={`Ingresar un nombre`}
                />
              </Col>
              <Col sm={6} lg={4} className="mb-4">
                <Input
                  id={"sub_tipo"}
                  title="Sub tipo"
                  required
                  onChange={handleChange}
                  onBlur={handleBlur}
                  values={values}
                  errors={errors}
                  touched={touched}
                  variant="gray-container"
                  size="lg"
                  disabled={!values.con_sub_tipo}
                />
              </Col>

              <Col sm={6} className="mb-4">
                <AsyncSelect
                  onChange={handleChange}
                  id="area_id"
                  values={values}
                  errors={errors}
                  touched={touched}
                  onBlur={handleBlur}
                  placeholder={"Área de conocimiento"}
                  size="lg"
                  title="Área de conocimiento"
                  getter={getArea}
                  paramKey={"nombre"}
                  variant="gray-container"
                  arrElementsPath="elementos"
                  optionsFormatConfig={{
                    value: "id",
                    label: (el: AreaI) => `${el.nombre}`,
                  }}
                  defaultSelectedOptions={
                    defaultOptionsSelected["default_area"]
                  }
                  required
                  noOptionsMessage={`No hay opciones disponibles para el nombre ingresado`}
                  emptySelectMessage={`Ingresar un nombre`}
                />
              </Col>
              <Row>
                <Col sm={6} className="mb-4">
                  <AsyncSelect
                    onChange={handleChange}
                    id="nivel_id"
                    values={values}
                    errors={errors}
                    touched={touched}
                    onBlur={handleBlur}
                    placeholder={"Buscar nombre del nivel"}
                    size="lg"
                    title="Nivel"
                    getter={getNiveles}
                    paramKey={"nombre"}
                    variant="gray-container"
                    arrElementsPath="elementos"
                    optionsFormatConfig={{
                      value: "id",
                      label: (el: NivelI) => `${el.nombre}`,
                    }}
                    defaultSelectedOptions={
                      defaultOptionsSelected["default_selected_nivel"]
                    }
                    required
                    noOptionsMessage={`No hay opciones disponibles para el nombre ingresado`}
                    emptySelectMessage={`Ingresar un nombre`}
                  />
                </Col>
              </Row>
              <Row className="">
                <Col sm={6} className="mb-3">
                  <AsyncSelect
                    onChange={handleChange}
                    id="ciclo_id"
                    values={values}
                    errors={errors}
                    touched={touched}
                    onBlur={handleBlur}
                    placeholder={"Buscar nombre de ciclo"}
                    size="lg"
                    title="Ciclo"
                    getter={getCiclo}
                    paramKey={"nombre"}
                    variant="gray-container"
                    arrElementsPath="elementos"
                    optionsFormatConfig={{
                      value: "id",
                      label: (el: CicloI) =>
                        `${el.nombre} (Año inicial: ${el.anioInicial} - Año final: ${el.anioFinal})`,
                    }}
                    defaultSelectedOptions={
                      defaultOptionsSelected["default_selected_ciclo"]
                    }
                    noOptionsMessage={`No hay opciones disponibles para el nombre ingresado`}
                    emptySelectMessage={`Ingresar un nombre`}
                  />
                </Col>
                {/* <Col>
                  <Button
                    onClick={() => handleCreateModal("ciclos")}
                    variant="secondary"
                    size={"lg"}
                    style={{
                      width: "175px",
                      height: "50px",
                      marginTop: "20px",
                      borderRadius: "8px",
                      fontSize: "1rem",
                    }}
                  >
                    Nuevo ciclo
                  </Button>
                </Col> */}
              </Row>
            </Row>
            <hr></hr>
            <Row>
              <Col>
                <div className="w-100 d-flex column-gap-3 justify-content-end">
                  <Button
                    variant="secondary"
                    size={"lg"}
                    onClick={handleReturn}
                  >
                    Cancelar
                  </Button>
                  <Button
                    variant="primary"
                    type="submit"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? "Confirmando..." : "Confirmar"}
                  </Button>
                </div>
              </Col>
            </Row>
          </form>
        </Card>
      </section>
    </>
  );
};
